import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, FileText, Eye, Save, Loader, CheckCircle, AlertCircle, Sparkles, Trash2, X, Edit3, Clock, Star, History, Wand2, FileSearch, Bot, ArrowRight, ChevronDown, Instagram, Music, RefreshCw, Globe } from 'lucide-react';
import XIcon from '../XIcon';
import { useNavigate } from 'react-router-dom';
import { 
    uploadBrandDocuments, 
    generateSystemPrompt, 
    savePrompt, 
    getUploadedDocuments,
    deleteDocument,
    getPromptHistory,
    updatePrompt,
    activatePrompt,
    deletePrompt
} from '../../services/brandVoiceApi';

interface BrandDocument {
    id: number;
    filename: string;
    word_count?: number;
    char_count?: number;
    file_type?: string;
    uploaded_at?: string;
}

interface BrandMode {
    id: number;
    name: string;
    system_prompt: string;
    brand_summary?: string;
    key_terms?: string[];
    tone_attributes?: string[];
    platforms?: string[];
    is_active: boolean;
    created_at: string;
    updated_at?: string;
}

interface BrandVoiceSettingsProps {
    hideHeader?: boolean;
    initialStep?: 'upload' | 'platforms' | 'generate' | 'preview' | 'history';
    onRegeneratePlatform?: (platform: string) => void;
    onBackToGeneration?: () => void;
}

const BrandVoiceSettings: React.FC<BrandVoiceSettingsProps> = ({ hideHeader = false, initialStep = 'upload', onRegeneratePlatform, onBackToGeneration }) => {
    const navigate = useNavigate();
    const [files, setFiles] = useState<File[]>([]);
    const [uploadedDocs, setUploadedDocs] = useState<BrandDocument[]>([]);
    const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
    const [generatedPrompts, setGeneratedPrompts] = useState<{ [platform: string]: { system_prompt: string; brand_summary?: string; key_terms?: string[]; tone_attributes?: string[] } }>({});
    const [brandSummary, setBrandSummary] = useState<string>('');
    const [keyTerms, setKeyTerms] = useState<string[]>([]);
    const [toneAttributes, setToneAttributes] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [step, setStep] = useState<'upload' | 'platforms' | 'generate' | 'preview' | 'history'>(initialStep);
    const [promptName, setPromptName] = useState('My Brand Voice');
    const [selectedVoices, setSelectedVoices] = useState<{ [platform: string]: boolean }>({}); // Which voices to save
    const [voiceNames, setVoiceNames] = useState<{ [platform: string]: string }>({}); // Names for each voice
    const [expandedVoices, setExpandedVoices] = useState<{ [platform: string]: boolean }>({}); // Which prompts are expanded
    const [platformFilter, setPlatformFilter] = useState<string>('UNIVERSAL'); // Filter for manage voices page
    const [brandModes, setBrandModes] = useState<BrandMode[]>([]);
    const [selectedUniversalVoiceId, setSelectedUniversalVoiceId] = useState<number | null>(null); // Which universal voice is selected
    const [platformToggles, setPlatformToggles] = useState<{ [platform: string]: boolean }>({}); // Toggle: use universal or platform-specific
    const [selectedPlatformVoiceIds, setSelectedPlatformVoiceIds] = useState<{ [platform: string]: number | null }>({}); // Selected voice per platform
    const [previousPlatformSelections, setPreviousPlatformSelections] = useState<{ [platform: string]: number | null }>({}); // Store previous selections before "Set All"
    const [showNoPlatformVoiceModal, setShowNoPlatformVoiceModal] = useState<string | null>(null); // Which platform to show modal for
    const [favoriteModes, setFavoriteModes] = useState<Set<number>>(new Set()); // Track favorite brand voices by ID
    const hasInitializedToggles = useRef(false); // Track if we've initialized toggles from backend
    const [editingMode, setEditingMode] = useState<BrandMode | null>(null);
    const [editName, setEditName] = useState('');
    const [editSystemPrompt, setEditSystemPrompt] = useState('');
    const [editPlatforms, setEditPlatforms] = useState<string[]>([]);
    const [showEditPlatformDropdown, setShowEditPlatformDropdown] = useState(false);
    const [manualInstructions, setManualInstructions] = useState('');
    const [manualRules, setManualRules] = useState('');
    const [showInstructionsInput, setShowInstructionsInput] = useState(false);
    const [showRulesInput, setShowRulesInput] = useState(false);
    const [platforms, setPlatforms] = useState<string[]>([]);
    const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
    const [isUniversalMode, setIsUniversalMode] = useState(false);
    
    // Platform-specific rules for custom generation
    const [platformRules, setPlatformRules] = useState<{ [platform: string]: string }>({});
    const [showPlatformRulesInput, setShowPlatformRulesInput] = useState<{ [platform: string]: boolean }>({});

    const handleAuthError = useCallback(() => {
        localStorage.removeItem('access_token');
        navigate('/login');
    }, [navigate]);


    const loadUploadedDocuments = useCallback(async () => {
        try {
            const result = await getUploadedDocuments();
            if (result.success) {
                setUploadedDocs(result.documents);
                // Don't auto-advance - let user navigate explicitly via "Proceed" button
            }
        } catch (err: any) {
            if (err.response?.status === 401) {
                handleAuthError();
            }
            console.error('Failed to load documents:', err);
        }
    }, [handleAuthError]);

    const loadBrandModes = useCallback(async () => {
        try {
            console.log('📚 LOAD: Fetching brand modes from API...');
            const result = await getPromptHistory();
            console.log('📚 LOAD: API Response:', result);
            if (result.success) {
                console.log('📚 LOAD: Number of prompts loaded:', result.prompts.length);
                console.log('📚 LOAD: Detailed prompts:', result.prompts.map((p: any) => ({
                    id: p.id,
                    name: p.name,
                    platforms: p.platforms,
                    is_active: p.is_active
                })));
                setBrandModes(result.prompts);
            }
        } catch (err: any) {
            console.error('📚 LOAD ERROR:', err);
            if (err.response?.status === 401) handleAuthError();
            console.error('Failed to load brand modes:', err);
        }
    }, [handleAuthError]);

    useEffect(() => {
        console.log('🔄 Component mounted or dependencies changed');
        loadUploadedDocuments();
        loadBrandModes();
        
        // Reset initialization flag to ensure fresh state
        hasInitializedToggles.current = false;
    }, [loadUploadedDocuments, loadBrandModes]);

    // Reset initialization flag whenever we're on history step with loaded brand modes
    // This ensures selections are always synced with backend when returning to this view
    useEffect(() => {
        if (brandModes.length > 0 && step === 'history') {
            console.log('🔄 History step with loaded brand modes - resetting init flag for fresh initialization');
            hasInitializedToggles.current = false;
        }
    }, [brandModes.length, step]);

    // Initialize platform toggles and selections based on current active voices
    // Only run once on initial load, not every time brandModes changes
    useEffect(() => {
        if (brandModes.length === 0 || hasInitializedToggles.current) return;
        
        console.log('🚀 Initializing platform toggles from backend');
        
        // Find the universal voice (2+ platforms or no platforms)
        const universalVoice = brandModes.find(m => 
            m.is_active && (!m.platforms || m.platforms.length === 0 || m.platforms.length >= 2)
        );
        
        if (universalVoice) {
            setSelectedUniversalVoiceId(universalVoice.id);
        } else {
            setSelectedUniversalVoiceId(null);
        }
        
        // For each platform, determine if it's using universal or platform-specific
        const newToggles: { [platform: string]: boolean } = {};
        const newSelections: { [platform: string]: number | null } = {};
        
        ['INSTAGRAM', 'TIKTOK', 'TWITTER'].forEach(platform => {
            // Check if there's an ACTIVE platform-specific voice for this platform
            const activePlatformVoice = brandModes.find(m => 
                m.is_active && m.platforms && m.platforms.length === 1 && m.platforms.includes(platform)
            );
            
            // Find the most recent platform voice (active or not) for this platform
            const anyPlatformVoice = brandModes.find(m =>
                m.platforms && m.platforms.length === 1 && m.platforms.includes(platform)
            );
            
            if (activePlatformVoice) {
                // Platform has its own active voice - use it
                console.log(`📱 ${platform}: Has active platform voice (ID: ${activePlatformVoice.id})`);
                newToggles[platform] = false;
                newSelections[platform] = activePlatformVoice.id;
            } else if (universalVoice && universalVoice.is_active) {
                // Universal voice is active and no platform-specific voice is active
                // This means platform is using universal
                console.log(`📱 ${platform}: Using universal voice (ID: ${universalVoice.id})`);
                newToggles[platform] = true;
                newSelections[platform] = null; // Don't select any platform voice when using universal
            } else {
                // No active voice for this platform
                console.log(`📱 ${platform}: No active voice`);
                newToggles[platform] = false;
                newSelections[platform] = null;
            }
        });
        
        setPlatformToggles(newToggles);
        setSelectedPlatformVoiceIds(newSelections);
        hasInitializedToggles.current = true;
        
        console.log('✅ Initialized toggles:', newToggles);
        console.log('✅ Initialized selectedUniversalVoiceId:', universalVoice?.id || null);
    }, [brandModes]);

    // DEBUG: Monitor generatedPrompts state changes
    useEffect(() => {
        console.log('🔄 STATE CHANGE: generatedPrompts updated', {
            keys: Object.keys(generatedPrompts),
            count: Object.keys(generatedPrompts).length,
            data: generatedPrompts
        });
    }, [generatedPrompts]);

    // DEBUG: Monitor platforms state changes
    useEffect(() => {
        console.log('🔄 STATE CHANGE: platforms updated', {
            platforms,
            count: platforms.length,
            isUniversalMode
        });
    }, [platforms, isUniversalMode]);

    const removeSelectedFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selectedFiles = Array.from(e.target.files).slice(0, 5);
            
            const validFiles = selectedFiles.filter(file => {
                const ext = file.name.split('.').pop()?.toLowerCase();
                return ['pdf', 'txt', 'docx'].includes(ext || '');
            });

            if (validFiles.length !== selectedFiles.length) {
                setError('Some files were skipped. Only PDF, TXT, and DOCX files are allowed.');
                setTimeout(() => setError(null), 4000);
            }

            setFiles(validFiles);
        }
    };

    const handleUpload = async () => {
        if (files.length === 0) return;
        
        setLoading(true);
        setError(null);
        setSuccess(null);
        
        try {
            const result = await uploadBrandDocuments(files);
            setUploadedDocs(result.uploaded_files);
            setSuccess(`✅ ${result.total_files} file(s) uploaded! Select target platforms next.`);
            setFiles([]);
            setStep('platforms');
            setTimeout(() => setSuccess(null), 5000);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg || e).join(', ') : 'File upload failed. Please try again.');
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const [generatingMessage, setGeneratingMessage] = useState('');

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        
        try {
            if (isUniversalMode) {
                // Universal mode: Single generation call for all platforms
                setGeneratingMessage('Generating universal brand voice...');
                
                const result = await generateSystemPrompt(
                    manualInstructions,
                    platformRules['UNIVERSAL'] || manualRules,
                    null  // No specific platform
                );
                
                setGeneratedPrompt(result.system_prompt);
                setBrandSummary(result.brand_summary || '');
                setKeyTerms(result.key_terms || []);
                setToneAttributes(result.tone_attributes || []);
                
                // Store in generatedPrompts for preview step
                const universalPrompts = {
                    'UNIVERSAL': result
                };
                setGeneratedPrompts(universalPrompts);
                
                // Initialize selection state for universal voice
                setSelectedVoices({ 'UNIVERSAL': true });
                setVoiceNames({ 'UNIVERSAL': 'My Brand Voice' });
                
                setStep('preview');
                
                const time = result.processing_time || '';
                setSuccess(`✅ Universal brand voice generated! ${time ? `(${time})` : ''}`);
                setTimeout(() => setSuccess(null), 5000);
                
            } else {
                // Individual platforms mode: Multiple generation calls
                const totalPlatforms = platforms.length;
                const allGeneratedPrompts: { [platform: string]: any } = {};
                
                console.log('🚀 GENERATION START:', {
                    totalPlatforms,
                    platforms,
                    isUniversalMode
                });
                
                for (let i = 0; i < totalPlatforms; i++) {
                    const platform = platforms[i];
                    const platformName = platform === 'INSTAGRAM' ? 'Instagram' 
                        : platform === 'TIKTOK' ? 'TikTok'
                        : platform === 'TWITTER' ? 'X'
                        : platform;
                    
                    setGeneratingMessage(`Generating ${platformName}... ${i + 1}/${totalPlatforms}`);
                    
                    console.log(`🎯 GENERATING ${i + 1}/${totalPlatforms}:`, { platform, platformName });
                    
                    try {
                        // Combine global rules with platform-specific rules
                        let combinedRules = manualRules || '';
                        if (platformRules[platform]) {
                            combinedRules = combinedRules 
                                ? `${combinedRules}\n\nPLATFORM-SPECIFIC RULES FOR ${platformName.toUpperCase()}:\n${platformRules[platform]}`
                                : platformRules[platform];
                        }
                        
                        console.log(`📤 API CALL for ${platformName}`);
                        
                        const result = await generateSystemPrompt(
                            manualInstructions,  // Global instructions apply to all
                            combinedRules,       // Global rules + platform-specific rules
                            platform             // Specify platform for customization
                        );
                        
                        console.log(`✅ API RESPONSE for ${platformName}:`, { hasPrompt: !!result.system_prompt });
                        
                        allGeneratedPrompts[platform] = result;
                        
                        console.log(`💾 STORED ${platformName} - Total: ${Object.keys(allGeneratedPrompts).length}`);
                        
                    } catch (err) {
                        console.error(`❌ FAILED ${platformName}:`, err);
                        throw new Error(`Failed to generate brand voice for ${platformName}`);
                    }
                }
                
                console.log('🏁 GENERATION COMPLETE:', {
                    totalGenerated: Object.keys(allGeneratedPrompts).length,
                    platforms: Object.keys(allGeneratedPrompts)
                });
                
                // Store ALL generated prompts
                setGeneratedPrompts(allGeneratedPrompts);
                
                // Initialize all voices as selected by default with default names
                const initialSelected: { [key: string]: boolean } = {};
                const initialNames: { [key: string]: string } = {};
                Object.keys(allGeneratedPrompts).forEach(platform => {
                    initialSelected[platform] = true; // Select all by default
                    // Map platform to display name
                    const platformName = platform === 'INSTAGRAM' ? 'Instagram' 
                                      : platform === 'TIKTOK' ? 'TikTok'
                                      : platform === 'TWITTER' ? 'X'
                                      : platform;
                    initialNames[platform] = `My Brand Voice - ${platformName}`;
                });
                setSelectedVoices(initialSelected);
                setVoiceNames(initialNames);
                
                console.log('🎯 DEBUG: Generated prompts:', allGeneratedPrompts);
                console.log('🎯 DEBUG: Number of platforms:', Object.keys(allGeneratedPrompts).length);
                console.log('🎯 DEBUG: Platforms array:', platforms);
                console.log('🎯 DEBUG: Universal mode:', isUniversalMode);
                
                // For backward compatibility, also set the first one as primary
                const firstPlatform = platforms[0];
                const firstResult = allGeneratedPrompts[firstPlatform];
                setGeneratedPrompt(firstResult.system_prompt);
                setBrandSummary(firstResult.brand_summary || '');
                setKeyTerms(firstResult.key_terms || []);
                setToneAttributes(firstResult.tone_attributes || []);
                
                console.log('🎯 DEBUG: Setting first platform as active:', firstPlatform);
                
                setStep('preview');
                
                setSuccess(`✅ Generated ${totalPlatforms} unique brand voice${totalPlatforms > 1 ? 's' : ''}!`);
                setTimeout(() => setSuccess(null), 5000);
            }
            
        } catch (err: any) {
            const detail = err.response?.data?.detail || err.message;
            const errorMsg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg || e).join(', ') : 'Prompt generation failed. Please ensure you have uploaded brand documents.');
            setError(errorMsg);
        } finally {
            setLoading(false);
            setGeneratingMessage('');
        }
    };

    const togglePlatform = (platform: string) => {
        setPlatforms(prev => 
            prev.includes(platform) 
                ? prev.filter(p => p !== platform)
                : [...prev, platform]
        );
    };

    const getExistingPlatformVoices = () => {
        const activeModes = brandModes.filter(m => m.is_active);
        const result: { [key: string]: BrandMode[] } = {};
        
        activeModes.forEach(mode => {
            if (mode.platforms && mode.platforms.length > 0) {
                mode.platforms.forEach(plat => {
                    if (!result[plat]) result[plat] = [];
                    result[plat].push(mode);
                });
            }
        });
        
        return result;
    };

    const hasConflicts = () => {
        const existing = getExistingPlatformVoices();
        return platforms.some(p => existing[p] && existing[p].length > 0);
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        
        try {
            console.log('💾 SAVE: Starting save process...');
            console.log('💾 SAVE: selectedVoices:', selectedVoices);
            console.log('💾 SAVE: voiceNames:', voiceNames);
            
            // Get only selected voices
            const selectedPlatforms = Object.keys(selectedVoices).filter(p => selectedVoices[p]);
            
            if (selectedPlatforms.length === 0) {
                setError('Please select at least one brand voice to save');
                setLoading(false);
                return;
            }
            
            // VALIDATE: Check for duplicate names BEFORE saving anything
            const namesToSave = selectedPlatforms.map(platform => {
                const platformDisplayName = platform === 'INSTAGRAM' ? 'Instagram'
                    : platform === 'TIKTOK' ? 'TikTok'
                    : platform === 'TWITTER' ? 'X'
                    : platform;
                return voiceNames[platform] || `My Brand Voice - ${platformDisplayName}`;
            });
            const existingNames = brandModes.map(m => m.name);
            const duplicates = namesToSave.filter(name => existingNames.includes(name));
            
            if (duplicates.length > 0) {
                console.log('❌ SAVE: Duplicate names detected:', duplicates);
                setError(`A prompt with the name "${duplicates[0]}" already exists. Please choose a different name.`);
                setLoading(false);
                return;
            }
            
            console.log(`💾 SAVE: Saving ${selectedPlatforms.length} selected platform(s)`);
            
            const savedPromptIds: { platform: string; id: number }[] = [];
            
            // Save each selected voice
            for (const platform of selectedPlatforms) {
                const platformData = generatedPrompts[platform];
                const platformDisplayName = platform === 'INSTAGRAM' ? 'Instagram'
                    : platform === 'TIKTOK' ? 'TikTok'
                    : platform === 'TWITTER' ? 'X'
                    : platform;
                const name = voiceNames[platform] || `My Brand Voice - ${platformDisplayName}`;
                
                // Determine platforms array based on platform key
                let platformsArray: string[];
                if (platform === 'UNIVERSAL') {
                    platformsArray = [];  // Empty array for universal voices
                } else {
                    platformsArray = [platform];  // Single platform for platform-specific
                }
                
                console.log(`💾 SAVE: Saving "${name}" for ${platform} with platforms:`, platformsArray);
                const result = await savePrompt(
                    name,
                    platformData.system_prompt,
                    platformData.brand_summary || '',
                    platformsArray
                );
                
                if (result.prompt_id) {
                    console.log(`💾 SAVE: Saved voice with ID ${result.prompt_id}`);
                    savedPromptIds.push({ platform, id: result.prompt_id });
                }
            }
            
            setSuccess(`✅ Saved ${selectedPlatforms.length} brand voice${selectedPlatforms.length > 1 ? 's' : ''}!`);
            await loadBrandModes();
            
            // Activate the newly saved voices
            console.log(`🔄 ACTIVATE: Activating ${savedPromptIds.length} newly saved voices`);
            for (const { platform, id } of savedPromptIds) {
                try {
                    console.log(`🔄 ACTIVATE: Activating ${platform} voice (ID: ${id})`);
                    await activatePrompt(id);
                    console.log(`✅ ACTIVATE: ${platform} voice activated`);
                } catch (activateErr: any) {
                    console.error(`❌ ACTIVATE ERROR for ${platform}:`, activateErr);
                    // Don't fail the whole operation if activation fails
                }
            }
            
            // Reload to get updated active states
            await loadBrandModes();
            console.log('✅ All brand modes reloaded with active states');
            
            // Navigate back to upload after 2 seconds
            setTimeout(() => {
                setSuccess(null);
                setStep('upload');
                setGeneratedPrompt('');
                setGeneratedPrompts({});
                setBrandSummary('');
                setManualInstructions('');
                setManualRules('');
                setPlatforms([]);
                setIsUniversalMode(false);
                setPlatformRules({});
                setSelectedVoices({});
                setVoiceNames({});
                setExpandedVoices({});
            }, 2000);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg || e).join(', ') : 'Failed to save brand voice');
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleDiscardAll = () => {
        // Reset all state and go back to Step 1
        setStep('upload');
        setGeneratedPrompt('');
        setGeneratedPrompts({});
        setBrandSummary('');
        setManualInstructions('');
        setManualRules('');
        setPlatforms([]);
        setIsUniversalMode(false);
        setPlatformRules({});
        setSelectedVoices({});
        setVoiceNames({});
        setExpandedVoices({});
        setKeyTerms([]);
        setToneAttributes([]);
        setSuccess('❌ Discarded all brand voices');
        setTimeout(() => setSuccess(null), 2000);
    };

    const handleDeleteDocument = async (docId: number) => {
        try {
            await deleteDocument(docId);
            await loadUploadedDocuments();
            setSuccess('Document deleted successfully');
            setTimeout(() => setSuccess(null), 2000);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : 'Failed to delete document';
            setError(errorMsg);
            setTimeout(() => setError(null), 3000);
        }
    };

    const startEditMode = (mode: BrandMode) => {
        setEditingMode(mode);
        setEditName(mode.name);
        setEditSystemPrompt(mode.system_prompt);
        setEditPlatforms(mode.platforms || ['INSTAGRAM', 'TIKTOK']);
    };

    const handleUpdateMode = async () => {
        if (!editingMode) return;
        setLoading(true);
        setError(null);
        
        try {
            await updatePrompt(editingMode.id, { 
                prompt_name: editName, 
                system_prompt: editSystemPrompt,
                platforms: editPlatforms
            });
            setSuccess('Brand voice mode updated successfully!');
            setEditingMode(null);
            await loadBrandModes();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : 'Failed to update brand voice mode';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMode = async (modeId: number) => {
        if (!window.confirm('Are you sure you want to delete this brand voice mode?')) return;
        
        setLoading(true);
        setError(null);
        
        try {
            await deletePrompt(modeId);
            setSuccess('Brand voice mode deleted!');
            await loadBrandModes();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : 'Failed to delete brand voice mode';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const toggleFavorite = (modeId: number) => {
        setFavoriteModes(prev => {
            const newFavorites = new Set(prev);
            if (newFavorites.has(modeId)) {
                newFavorites.delete(modeId);
            } else {
                newFavorites.add(modeId);
            }
            // Store in localStorage for persistence
            localStorage.setItem('favoriteBrandVoices', JSON.stringify(Array.from(newFavorites)));
            return newFavorites;
        });
    };

    // Handle selecting a universal voice (radio button)
    const handleSelectUniversalVoice = async (voiceId: number) => {
        console.log('═══════════════════════════════════════════');
        console.log('🎯 UNIVERSAL VOICE CHECKBOX CLICKED');
        console.log('Voice ID:', voiceId);
        console.log('Current selectedUniversalVoiceId:', selectedUniversalVoiceId);
        console.log('═══════════════════════════════════════════');
        
        // Simple: Set this as selected, activate it
        setSelectedUniversalVoiceId(voiceId);
        
        setLoading(true);
        setError(null);
        
        try {
            console.log('📡 Activating universal voice:', voiceId);
            await activatePrompt(voiceId);
            console.log('✅ Voice activated');
            
            console.log('🔄 Reloading brand modes...');
            await loadBrandModes();
            console.log('✅ Brand modes reloaded');
        } catch (err: any) {
            console.error('❌ Error activating universal voice:', err);
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : 'Failed to activate universal voice';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Handle toggling platform to use universal or platform-specific
    const handlePlatformToggle = async (platform: string, useUniversal: boolean) => {
        console.log('═══════════════════════════════════════════');
        console.log('🔘 "USE UNIVERSAL" CHECKBOX CLICKED');
        console.log('Platform:', platform);
        console.log('Checking (useUniversal):', useUniversal);
        console.log('Current platformToggles:', platformToggles);
        console.log('Current selectedPlatformVoiceIds:', selectedPlatformVoiceIds);
        console.log('Current selectedUniversalVoiceId:', selectedUniversalVoiceId);
        console.log('═══════════════════════════════════════════');
        
        // Update the toggle state
        setPlatformToggles(prev => {
            const newState = { ...prev, [platform]: useUniversal };
            console.log('Updated platformToggles:', newState);
            return newState;
        });
        
        if (useUniversal) {
            // User checked "Use Universal"
            // UNCHECK the platform voice checkbox for this platform
            console.log('✂️ Unchecking platform voice for', platform);
            
            // Find any active platform-specific voice for this platform and deactivate it
            const activePlatformVoice = brandModes.find(m => 
                m.is_active && m.platforms && m.platforms.length === 1 && m.platforms.includes(platform)
            );
            
            setSelectedPlatformVoiceIds(prev => {
                const newState = { ...prev, [platform]: null };
                console.log('Updated selectedPlatformVoiceIds (cleared):', newState);
                return newState;
            });
            
            if (selectedUniversalVoiceId) {
                setLoading(true);
                setError(null);
                
                try {
                    // First, deactivate the platform-specific voice if it exists
                    if (activePlatformVoice) {
                        console.log('📡 Will deactivate platform voice:', activePlatformVoice.id, activePlatformVoice.name);
                    }
                    
                    console.log('📡 Activating universal voice:', selectedUniversalVoiceId);
                    console.log('📡 Deactivating voices for platform:', platform);
                    await activatePrompt(selectedUniversalVoiceId, platform);
                    console.log('✅ Universal voice activated, platform voice deactivated');
                    
                    console.log('🔄 Reloading brand modes...');
                    await loadBrandModes();
                    console.log('✅ Brand modes reloaded');
                } catch (err: any) {
                    console.error('❌ Error activating universal voice:', err);
                    const detail = err.response?.data?.detail;
                    const errorMsg = typeof detail === 'string' ? detail : 'Failed to activate universal voice';
                    setError(errorMsg);
                } finally {
                    setLoading(false);
                }
            } else {
                console.log('⚠️ No universal voice selected yet');
            }
        } else {
            // User unchecked "Use Universal"
            if (selectedPlatformVoiceIds[platform]) {
                setLoading(true);
                setError(null);
                
                try {
                    console.log('📡 Activating platform voice:', selectedPlatformVoiceIds[platform]);
                    await activatePrompt(selectedPlatformVoiceIds[platform]!);
                    console.log('✅ Platform voice activated');
                    
                    console.log('🔄 Reloading brand modes...');
                    await loadBrandModes();
                    console.log('✅ Brand modes reloaded');
                } catch (err: any) {
                    console.error('❌ Error activating platform voice:', err);
                    const detail = err.response?.data?.detail;
                    const errorMsg = typeof detail === 'string' ? detail : 'Failed to activate platform voice';
                    setError(errorMsg);
                } finally {
                    setLoading(false);
                }
            } else {
                console.log('ℹ️ No platform voice selected for', platform);
            }
        }
    };

    // Handle setting all platforms to use universal voice
    const handleSetAllPlatformsToUniversal = async (useUniversal: boolean) => {
        console.log('═══════════════════════════════════════════');
        console.log('🌐 SET ALL PLATFORMS TO UNIVERSAL CLICKED');
        console.log('Setting to:', useUniversal);
        console.log('Current selectedUniversalVoiceId:', selectedUniversalVoiceId);
        console.log('═══════════════════════════════════════════');

        if (useUniversal && !selectedUniversalVoiceId) {
            console.log('⚠️ No universal voice selected');
            setError('Please select a universal voice first');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const platforms = ['INSTAGRAM', 'TIKTOK', 'TWITTER'];
            
            if (useUniversal) {
                // SAVE current platform selections before switching to universal
                console.log('💾 Saving current platform selections before switching to universal');
                const currentSelections: { [platform: string]: number | null } = {};
                platforms.forEach(platform => {
                    // Only save if the platform has a voice selected (not using universal)
                    if (!platformToggles[platform] && selectedPlatformVoiceIds[platform]) {
                        currentSelections[platform] = selectedPlatformVoiceIds[platform];
                        console.log(`  💾 Saved ${platform}: ${selectedPlatformVoiceIds[platform]}`);
                    }
                });
                setPreviousPlatformSelections(currentSelections);
                
                // Set all platforms to use universal
                console.log('📡 Setting all platforms to use universal voice');
                
                // Update UI state for all platforms
                const newToggles: { [platform: string]: boolean } = {};
                const newSelections: { [platform: string]: number | null } = {};
                
                platforms.forEach(platform => {
                    newToggles[platform] = true;
                    newSelections[platform] = null;
                });
                
                setPlatformToggles(newToggles);
                setSelectedPlatformVoiceIds(newSelections);
                
                // Activate universal voice and deactivate all platform-specific voices
                const platformsToDeactivate = platforms.join(',');
                console.log('📡 Activating universal voice with deactivate_platforms:', platformsToDeactivate);
                await activatePrompt(selectedUniversalVoiceId!, platformsToDeactivate);
                console.log('✅ Universal voice activated for all platforms');
            } else {
                // RESTORE previous platform selections
                console.log('🔄 Restoring previous platform selections');
                console.log('Previous selections:', previousPlatformSelections);
                
                const newToggles: { [platform: string]: boolean } = {};
                const newSelections: { [platform: string]: number | null } = {};
                
                // Restore each platform's previous selection
                for (const platform of platforms) {
                    const previousVoiceId = previousPlatformSelections[platform];
                    
                    if (previousVoiceId) {
                        // Had a previous selection - restore it
                        console.log(`  🔄 Restoring ${platform}: ${previousVoiceId}`);
                        newToggles[platform] = false;
                        newSelections[platform] = previousVoiceId;
                        
                        // Activate the previous voice
                        try {
                            await activatePrompt(previousVoiceId);
                            console.log(`  ✅ Activated ${platform} voice ${previousVoiceId}`);
                        } catch (err) {
                            console.error(`  ❌ Failed to activate ${platform} voice:`, err);
                        }
                    } else {
                        // No previous selection - leave unchecked
                        console.log(`  ℹ️ No previous selection for ${platform}`);
                        newToggles[platform] = false;
                        newSelections[platform] = null;
                    }
                }
                
                setPlatformToggles(newToggles);
                setSelectedPlatformVoiceIds(newSelections);
            }
            
            await loadBrandModes();
            console.log('✅ Brand modes reloaded');
        } catch (err: any) {
            console.error('❌ Error setting all platforms to universal:', err);
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : 'Failed to set all platforms to universal';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Handle selecting a platform-specific voice (checkbox)
    const handleSelectPlatformVoice = async (platform: string, voiceId: number) => {
        console.log('═══════════════════════════════════════════');
        console.log('🎯 PLATFORM VOICE CHECKBOX CLICKED');
        console.log('Platform:', platform);
        console.log('Voice ID:', voiceId);
        console.log('Current brandModes count:', brandModes.length);
        console.log('All brand voices BEFORE click:', brandModes.map(m => ({ 
            id: m.id, 
            name: m.name, 
            platforms: m.platforms, 
            is_active: m.is_active 
        })));
        console.log('═══════════════════════════════════════════');
        
        // Simple: Set this voice as selected for this platform
        setSelectedPlatformVoiceIds(prev => {
            const newState = { ...prev, [platform]: voiceId };
            console.log('Updated selectedPlatformVoiceIds:', newState);
            return newState;
        });
        
        // ALSO save this as the most recent selection for this platform
        setPreviousPlatformSelections(prev => {
            const newState = { ...prev, [platform]: voiceId };
            console.log('💾 Updated previousPlatformSelections:', newState);
            return newState;
        });
        
        // UNCHECK "Use Universal" for this platform
        console.log('✂️ Unchecking "Use Universal" for', platform);
        setPlatformToggles(prev => {
            const newState = { ...prev, [platform]: false };
            console.log('Updated platformToggles:', newState);
            return newState;
        });
        
        setLoading(true);
        setError(null);
        
        try {
            console.log('📡 Activating platform voice:', voiceId);
            const voice = brandModes.find(m => m.id === voiceId);
            console.log('Voice details BEFORE activation:', voice ? { id: voice.id, name: voice.name, platforms: voice.platforms } : 'not found');
            
            console.log('🌐 CALLING BACKEND API: activatePrompt(' + voiceId + ')');
            await activatePrompt(voiceId);
            console.log('✅ Platform voice activated - backend responded successfully');
            
            console.log('🔄 Reloading brand modes from backend...');
            await loadBrandModes();
            console.log('✅ Brand modes reloaded');
            console.log('📊 Brand modes count AFTER reload:', brandModes.length);
            console.log('All brand voices AFTER reload:', brandModes.map(m => ({ 
                id: m.id, 
                name: m.name, 
                platforms: m.platforms, 
                is_active: m.is_active 
            })));
            
            // Check if the voice we just activated still exists
            const voiceStillExists = brandModes.find(m => m.id === voiceId);
            if (voiceStillExists) {
                console.log('✅ Voice ID', voiceId, 'STILL EXISTS after reload');
            } else {
                console.log('🚨 WARNING: Voice ID', voiceId, 'DISAPPEARED after reload!');
            }
        } catch (err: any) {
            console.error('❌ Error activating platform voice:', err);
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : 'Failed to activate platform voice';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Apply the selected voice for a platform
    const applyPlatformVoiceSelection = async (platform: string, voiceId?: number, explicitUseUniversal?: boolean) => {
        console.log('───────────────────────────────────────────');
        console.log('⚙️ applyPlatformVoiceSelection CALLED');
        console.log('Platform:', platform);
        console.log('Provided voiceId:', voiceId);
        console.log('explicitUseUniversal:', explicitUseUniversal);
        console.log('platformToggles[platform]:', platformToggles[platform]);
        console.log('selectedUniversalVoiceId:', selectedUniversalVoiceId);
        console.log('selectedPlatformVoiceIds[platform]:', selectedPlatformVoiceIds[platform]);
        console.log('───────────────────────────────────────────');
        
        setLoading(true);
        setError(null);
        
        try {
            // Use explicit value if provided (to avoid race condition), otherwise use state
            const useUniversal = explicitUseUniversal !== undefined ? explicitUseUniversal : platformToggles[platform];
            console.log('🎯 ACTUAL useUniversal value being used:', useUniversal);
            
            if (useUniversal && selectedUniversalVoiceId) {
                console.log('🌐 Using UNIVERSAL voice');
                // Use universal voice for this platform
                const universalVoice = brandModes.find(m => m.id === selectedUniversalVoiceId);
                console.log('Found universal voice:', universalVoice ? { id: universalVoice.id, name: universalVoice.name, is_active: universalVoice.is_active } : 'null');
                
                if (universalVoice) {
                    // Activate universal if not already active
                    if (!universalVoice.is_active) {
                        console.log('📡 Activating universal voice:', selectedUniversalVoiceId);
                        await activatePrompt(selectedUniversalVoiceId);
                        console.log('✅ Universal voice activated');
                    } else {
                        console.log('ℹ️ Universal voice already active');
                    }
                }
                setSuccess(`${platform} now using universal voice!`);
                console.log('🔄 Reloading brand modes...');
                await loadBrandModes();
                console.log('✅ Brand modes reloaded');
            } else {
                console.log('📱 Using PLATFORM-SPECIFIC voice');
                // Use platform-specific voice
                const selectedVoiceId = voiceId || selectedPlatformVoiceIds[platform];
                console.log('Selected voice ID:', selectedVoiceId);
                
                if (selectedVoiceId) {
                    console.log('📡 Activating platform voice:', selectedVoiceId);
                    await activatePrompt(selectedVoiceId);
                    console.log('✅ Platform voice activated');
                    
                    console.log('🔄 Reloading brand modes...');
                    await loadBrandModes();
                    console.log('✅ Brand modes reloaded');
                } else {
                    // No platform voice selected - don't reload, user needs to select one
                    console.log('⚠️ No platform voice selected - waiting for user to select');
                }
            }
        } catch (err: any) {
            console.error('❌ Error in applyPlatformVoiceSelection:', err);
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : 'Failed to apply voice selection';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Load favorites from localStorage on mount
    useEffect(() => {
        const savedFavorites = localStorage.getItem('favoriteBrandVoices');
        if (savedFavorites) {
            try {
                const favoriteIds = JSON.parse(savedFavorites);
                setFavoriteModes(new Set(favoriteIds));
            } catch (e) {
                console.error('Failed to load favorites:', e);
            }
        }
    }, []);

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            
            
            {/* Header - Only show if not hidden */}
            {!hideHeader && (
                <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #4285f4 0%, #5c9ff4 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 12px rgba(66, 133, 244, 0.3)'
                            }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                                    <path d="M2 17l10 5 10-5"/>
                                    <path d="M2 12l10 5 10-5"/>
                                </svg>
                            </div>
                            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                                Brand Voice
                            </h2>
                        </div>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginLeft: '52px' }}>
                        Upload your brand docs to create your unique AI voice that reflects your brand's personality and tone
                    </p>
                </div>
            )}

            {error && (
                <div style={{
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: 'var(--radius-md)',
                    padding: '1rem',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                }}>
                    <AlertCircle size={20} color="#dc2626" />
                    <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</span>
                </div>
            )}

            {success && (
                <div style={{
                    backgroundColor: '#dcfce7',
                    border: '1px solid #bbf7d0',
                    borderRadius: 'var(--radius-md)',
                    padding: '1rem',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                }}>
                    <CheckCircle size={20} color="#16a34a" />
                    <span style={{ color: '#16a34a', fontSize: '0.875rem' }}>{success}</span>
                </div>
            )}


            {/* Progress Steps - Redesigned (hide in history view) */}
            {step !== 'history' && (
            <div style={{
                marginBottom: '3rem',
                position: 'relative',
                padding: '0 2rem'
            }}>
                {/* Progress Bar Background */}
                <div style={{
                    position: 'absolute',
                    top: '24px',
                    left: '56px',
                    right: '56px',
                    height: '3px',
                    backgroundColor: '#f1f5f9',
                    zIndex: 0,
                    borderRadius: '4px'
                }} />
                
                {/* Active Progress Bar */}
                <div style={{
                    position: 'absolute',
                    top: '24px',
                    left: '56px',
                    height: '3px',
                    backgroundColor: '#4285f4',
                    zIndex: 0,
                    borderRadius: '4px',
                    width: step === 'upload' ? '0%' : 
                           step === 'platforms' ? 'calc(33.33% - 37px)' : 
                           step === 'generate' ? 'calc(66.66% - 74px)' : 
                           'calc(100% - 112px)',
                    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }} />

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    position: 'relative',
                    zIndex: 1
                }}>
                    {/* Step 1: Upload */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', width: '80px' }}>
                        <div 
                            onClick={() => setStep('upload')}
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: step === 'upload' || step === 'platforms' || step === 'generate' || step === 'preview' ? '#4285f4' : 'white',
                                border: `2px solid ${step === 'upload' || step === 'platforms' || step === 'generate' || step === 'preview' ? '#4285f4' : '#e2e8f0'}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: step === 'upload' || step === 'platforms' || step === 'generate' || step === 'preview' ? 'white' : '#94a3b8',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: step === 'upload' ? '0 0 0 4px rgba(99, 102, 241, 0.2)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            {step !== 'upload' ? <CheckCircle size={24} /> : '1'}
                        </div>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: step === 'upload' ? '#4285f4' : '#64748b', whiteSpace: 'nowrap' }}>Upload</span>
                    </div>

                    {/* Step 2: Platforms */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', width: '80px' }}>
                        <div 
                            onClick={() => uploadedDocs.length > 0 && setStep('platforms')}
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: step === 'platforms' || step === 'generate' || step === 'preview' ? '#4285f4' : 'white',
                                border: `2px solid ${step === 'platforms' || step === 'generate' || step === 'preview' ? '#4285f4' : '#e2e8f0'}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: step === 'platforms' || step === 'generate' || step === 'preview' ? 'white' : '#94a3b8',
                                fontWeight: '600',
                                cursor: uploadedDocs.length > 0 ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s ease',
                                boxShadow: step === 'platforms' ? '0 0 0 4px rgba(99, 102, 241, 0.2)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            {step === 'generate' || step === 'preview' ? <CheckCircle size={24} /> : '2'}
                        </div>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: step === 'platforms' ? '#4285f4' : '#64748b', whiteSpace: 'nowrap' }}>Platforms</span>
                    </div>

                    {/* Step 3: Generate */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', width: '80px' }}>
                        <div 
                            onClick={() => uploadedDocs.length > 0 && platforms.length > 0 && setStep('generate')}
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: step === 'generate' || step === 'preview' ? '#4285f4' : 'white',
                                border: `2px solid ${step === 'generate' || step === 'preview' ? '#4285f4' : '#e2e8f0'}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: step === 'generate' || step === 'preview' ? 'white' : '#94a3b8',
                                fontWeight: '600',
                                cursor: (uploadedDocs.length > 0 && platforms.length > 0) ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s ease',
                                boxShadow: step === 'generate' ? '0 0 0 4px rgba(99, 102, 241, 0.2)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            {step === 'preview' ? <CheckCircle size={24} /> : '3'}
                        </div>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: step === 'generate' ? '#4285f4' : '#64748b', whiteSpace: 'nowrap' }}>Generate</span>
                    </div>

                    {/* Step 4: Preview */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', width: '80px' }}>
                        <div 
                            onClick={() => generatedPrompt && setStep('preview')}
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: step === 'preview' ? '#4285f4' : 'white',
                                border: `2px solid ${step === 'preview' ? '#4285f4' : '#e2e8f0'}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: step === 'preview' ? 'white' : '#94a3b8',
                                fontWeight: '600',
                                cursor: generatedPrompt ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s ease',
                                boxShadow: step === 'preview' ? '0 0 0 4px rgba(99, 102, 241, 0.2)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            4
                        </div>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: step === 'preview' ? '#4285f4' : '#64748b', whiteSpace: 'nowrap' }}>Preview</span>
                    </div>
                </div>
            </div>
            )}

            {step === 'upload' && (
                <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
                    <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.75rem' }}>
                            Upload Brand Assets
                        </h3>
                        <p style={{ color: '#64748b', maxWidth: '500px', margin: '0 auto', lineHeight: '1.6' }}>
                            Upload your brand guidelines, mission statement, and tone of voice documents. 
                            We support PDF, TXT, and DOCX formats.
                        </p>
                    </div>

                    {/* Drop Zone - Professional */}
                    <div style={{
                        border: '2px dashed #cbd5e1',
                        borderRadius: '16px',
                        padding: '3rem 2rem',
                        textAlign: 'center',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        marginBottom: '2rem'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#4285f4';
                        e.currentTarget.style.backgroundColor = '#e8f0fe';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}>
                        <input
                            type="file"
                            multiple
                            accept=".pdf,.txt,.docx"
                            onChange={handleFileChange}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                opacity: 0,
                                cursor: 'pointer',
                                zIndex: 1
                            }}
                        />
                        <div style={{ 
                            width: '64px', 
                            height: '64px', 
                            borderRadius: '50%', 
                            backgroundColor: '#e8f0fe', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            margin: '0 auto 1.5rem'
                        }}>
                            <Upload size={32} color="#4285f4" />
                        </div>
                        <p style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                            Click to upload or drag and drop
                        </p>
                        <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                            PDF, DOCX, TXT (Max 10MB)
                        </p>
                    </div>

                    {/* Selected Files List - Professional */}
                    {files.length > 0 && (
                        <div style={{ marginBottom: '2rem' }}>
                            <div style={{ 
                                fontSize: '0.875rem', 
                                fontWeight: '600', 
                                color: '#475569', 
                                marginBottom: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <span>Ready to Upload ({files.length})</span>
                                <button
                                    onClick={() => setFiles([])}
                                    style={{
                                        fontSize: '0.8rem',
                                        color: '#ef4444',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Remove All
                                </button>
                            </div>
                            {files.map((file, i) => (
                                <div key={i} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    padding: '1rem',
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    marginBottom: '0.75rem',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    animation: 'slideUp 0.3s ease-out'
                                }}>
                                    <div style={{
                                        padding: '0.75rem',
                                        backgroundColor: '#e8f0fe',
                                        borderRadius: '8px'
                                    }}>
                                        <FileText size={20} color='#4285f4' />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1e293b', marginBottom: '0.25rem' }}>
                                            {file.name}
                                        </div>
                                        <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                                            {(file.size / 1024).toFixed(1)} KB • Ready
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => removeSelectedFile(i)}
                                        style={{
                                            padding: '0.5rem',
                                            backgroundColor: '#fee2e2',
                                            border: 'none',
                                            borderRadius: '8px',
                                            color: '#dc2626',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s'
                                        }}
                                        title="Remove file"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <button 
                        onClick={() => uploadedDocs.length > 0 ? setStep('platforms') : handleUpload()}
                        disabled={(files.length === 0 && uploadedDocs.length === 0) || loading}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.75rem',
                            padding: '1rem',
                            fontSize: '1rem',
                            fontWeight: '600',
                            backgroundColor: (files.length > 0 || uploadedDocs.length > 0) && !loading ? '#4285f4' : '#e2e8f0',
                            color: (files.length > 0 || uploadedDocs.length > 0) && !loading ? 'white' : '#94a3b8',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: (files.length > 0 || uploadedDocs.length > 0) && !loading ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s ease',
                            boxShadow: (files.length > 0 || uploadedDocs.length > 0) && !loading ? '0 4px 12px rgba(66, 133, 244, 0.3)' : 'none'
                        }}
                    >
                        {loading ? <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> : <ArrowRight size={20} />}
                        {loading ? 'Uploading Files...' : files.length > 0 ? 'Upload & Continue' : 'Continue to Platforms'}
                    </button>

                    {uploadedDocs.length > 0 && (
                        <div style={{ marginTop: '2rem' }}>
                            <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>Uploaded Documents</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {uploadedDocs.map(doc => (
                                    <div key={doc.id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        padding: '1rem',
                                        backgroundColor: 'white',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border-color)'
                                    }}>
                                        <FileText size={20} color='var(--primary-color)' />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1e293b', marginBottom: '0.25rem' }}>
                                                {doc.filename}
                                            </div>
                                            {doc.word_count && (
                                                <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                                    {doc.word_count.toLocaleString()} words
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleDeleteDocument(doc.id)}
                                            title="Delete Document"
                                            className="btn-icon-danger"
                                            style={{
                                                padding: '0.5rem',
                                                backgroundColor: '#fee2e2',
                                                border: 'none',
                                                borderRadius: '8px',
                                                color: '#dc2626',
                                                cursor: 'pointer',
                                                transition: 'color 0.2s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {step === 'platforms' && (
                <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
                    <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.75rem' }}>
                            Select Target Platforms
                        </h3>
                        <p style={{ color: '#64748b', maxWidth: '600px', margin: '0 auto', lineHeight: '1.6' }}>
                            Choose which platforms this brand voice will be used for. You can create a unified voice for all platforms or select individual platforms for tailored voices.
                        </p>
                    </div>

                    {/* Universal Voice Option */}
                    <div 
                        onClick={() => {
                            if (isUniversalMode) {
                                // Deselect universal
                                setIsUniversalMode(false);
                                setPlatforms([]);
                            } else {
                                // Select universal
                                setIsUniversalMode(true);
                                setPlatforms(['INSTAGRAM', 'TIKTOK']);
                            }
                        }}
                        style={{
                            border: `2px solid ${isUniversalMode ? '#4285f4' : '#e2e8f0'}`,
                            borderRadius: '16px',
                            padding: '1.5rem',
                            backgroundColor: isUniversalMode ? '#e8f0fe' : 'white',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            marginBottom: '2rem',
                            boxShadow: isUniversalMode ? '0 4px 12px rgba(99, 102, 241, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                        }}
                        onMouseEnter={(e) => {
                            if (!isUniversalMode) {
                                e.currentTarget.style.borderColor = '#cbd5e1';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isUniversalMode) {
                                e.currentTarget.style.borderColor = '#e2e8f0';
                            }
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            {/* Checkbox */}
                            <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '6px',
                                backgroundColor: isUniversalMode ? '#4285f4' : '#e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                flexShrink: 0
                            }}>
                                {isUniversalMode && (
                                    <CheckCircle size={16} color="white" />
                                )}
                            </div>

                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '1.125rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.25rem' }}>
                                    Universal Brand Voice (All Platforms)
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#64748b', lineHeight: '1.5' }}>
                                    Create one unified brand voice that will be used across Instagram and TikTok
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '1rem', 
                        marginBottom: '1.5rem',
                        color: '#94a3b8',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }} />
                        <span>OR</span>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }} />
                    </div>

                    {/* Individual Platform Selection */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            marginBottom: '1rem',
                            padding: '0.75rem 1rem',
                            backgroundColor: '#f1f5f9',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            color: '#475569',
                            lineHeight: '1.5'
                        }}>
                            <Sparkles size={16} color="#4285f4" style={{ flexShrink: 0 }} />
                            <span>
                                <strong>Platform-Specific Voices:</strong> Select individual platforms to create unique brand voices tailored to each platform's style and audience
                            </span>
                        </div>
                    </div>

                    {/* Platform Selection Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                        {['INSTAGRAM', 'TIKTOK', 'TWITTER'].map(platform => {
                            const isSelected = platforms.includes(platform) && !isUniversalMode;
                            const existing = getExistingPlatformVoices()[platform];
                            const hasExisting = existing && existing.length > 0;
                            
                            return (
                                <div
                                    key={platform}
                                    onClick={() => {
                                        // If universal is selected, clear it first
                                        if (isUniversalMode) {
                                            setIsUniversalMode(false);
                                            setPlatforms([platform]);
                                        } else {
                                            togglePlatform(platform);
                                        }
                                    }}
                                    style={{
                                        border: `2px solid ${isSelected ? '#4285f4' : hasExisting ? '#f59e0b' : '#e2e8f0'}`,
                                        borderRadius: '12px',
                                        padding: '1rem',
                                        backgroundColor: isSelected ? '#e8f0fe' : 'white',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                        boxShadow: isSelected ? '0 2px 8px rgba(66, 133, 244, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
                                        opacity: isUniversalMode ? 0.5 : 1,
                                        minHeight: '80px',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected && !isUniversalMode) e.currentTarget.style.borderColor = '#cbd5e1';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSelected && !isUniversalMode) e.currentTarget.style.borderColor = hasExisting ? '#f59e0b' : '#e2e8f0';
                                    }}
                                >
                                    {/* Checkmark Icon */}
                                    <div style={{
                                        position: 'absolute',
                                        top: '0.75rem',
                                        right: '0.75rem',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        backgroundColor: isSelected ? '#4285f4' : '#e2e8f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s'
                                    }}>
                                        {isSelected && <CheckCircle size={14} color="white" />}
                                    </div>

                                    {/* Platform Name */}
                                    <div style={{
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        color: '#1e293b',
                                        marginBottom: '0.25rem',
                                        paddingRight: '1.5rem'
                                    }}>
                                        {platform === 'INSTAGRAM' ? 'Instagram' : platform === 'TIKTOK' ? 'TikTok' : 'X'}
                                    </div>

                                    {/* Existing Voice Warning */}
                                    {hasExisting && !isUniversalMode && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '0.35rem',
                                            marginTop: '0.5rem',
                                            padding: '0.5rem',
                                            backgroundColor: '#fef3c7',
                                            borderRadius: '6px',
                                            fontSize: '0.7rem',
                                            color: '#92400e',
                                            lineHeight: '1.3'
                                        }}>
                                            <AlertCircle size={12} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                                            <div>
                                                <strong>Active:</strong> {existing[0].name}
                                            </div>
                                        </div>
                                    )}

                                    {/* Selection Indicator */}
                                    {isSelected && !hasExisting && (
                                        <div style={{
                                            marginTop: '0.5rem',
                                            padding: '0.4rem',
                                            backgroundColor: '#dbeafe',
                                            borderRadius: '4px',
                                            fontSize: '0.7rem',
                                            color: '#1e40af',
                                            textAlign: 'center',
                                            fontWeight: '500'
                                        }}>
                                            ✓ Unique voice
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Conflict Warning if any */}
                    {hasConflicts() && !isUniversalMode && (
                        <div style={{
                            backgroundColor: '#fef3c7',
                            border: '1px solid #fbbf24',
                            borderRadius: '12px',
                            padding: '1rem 1.25rem',
                            marginBottom: '2rem',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.75rem'
                        }}>
                            <AlertCircle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
                            <div style={{ fontSize: '0.875rem', color: '#92400e', lineHeight: '1.5' }}>
                                <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Existing brand voices will be replaced</strong>
                                The platforms you've selected already have active brand voices. Creating new voices for these platforms will replace existing ones. Previous brand voices will still be accessible in the manage mode.
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                            onClick={() => setStep('upload')}
                            style={{
                                flex: 1,
                                padding: '1rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                backgroundColor: 'white',
                                color: '#64748b',
                                border: '2px solid #e2e8f0',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#cbd5e1';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e2e8f0';
                            }}
                        >
                            ← Back
                        </button>
                        <button
                            onClick={() => platforms.length > 0 && setStep('generate')}
                            disabled={platforms.length === 0}
                            style={{
                                flex: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.75rem',
                                padding: '1rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                backgroundColor: platforms.length > 0 ? '#4285f4' : '#e2e8f0',
                                color: platforms.length > 0 ? 'white' : '#94a3b8',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: platforms.length > 0 ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s',
                                boxShadow: platforms.length > 0 ? '0 4px 12px rgba(66, 133, 244, 0.3)' : 'none'
                            }}
                        >
                            Continue to Generation
                            <ArrowRight size={20} />
                        </button>
                    </div>

                    {/* Selected Summary */}
                    {platforms.length > 0 && (
                        <div style={{
                            marginTop: '1.5rem',
                            padding: '1rem',
                            backgroundColor: '#f1f5f9',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            color: '#475569',
                            textAlign: 'center'
                        }}>
                            <strong style={{ color: '#1e293b' }}>Selected:</strong>{' '}
                            {isUniversalMode
                                ? 'Universal brand voice for Instagram and TikTok (1 generation)'
                                : `${platforms.map(p => 
                                    p === 'INSTAGRAM' ? 'Instagram' 
                                    : p === 'TIKTOK' ? 'TikTok'
                                    : p === 'TWITTER' ? 'X'
                                    : p
                                ).join(', ')} ${platforms.length > 1 ? `(${platforms.length} separate generations - unique voice for each)` : '(1 unique voice)'}`
                            }
                        </div>
                    )}
                </div>
            )}

            {step === 'generate' && (
                <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
                    <div style={{
                        backgroundColor: 'white',
                        border: '2px solid #e2e8f0',
                        borderRadius: '16px',
                        padding: '3rem 2rem',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        textAlign: 'center'
                    }}>
                        <div style={{ 
                            width: '80px', 
                            height: '80px', 
                            borderRadius: '50%', 
                            background: 'linear-gradient(135deg, #4285f4 0%, #5c9ff4 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 2rem',
                            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)'
                        }}>
                            {loading ? (
                                <Bot size={40} color="white" style={{ animation: 'pulse 2s infinite' }} />
                            ) : (
                                <Wand2 size={40} color="white" />
                            )}
                        </div>

                        <h3 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem', color: '#1e293b' }}>
                            {loading ? generatingMessage : 'Generate Brand Voice'}
                        </h3>
                        
                        <p style={{ color: '#64748b', fontSize: '1rem', marginBottom: '2.5rem', maxWidth: '600px', margin: '0 auto 2.5rem', lineHeight: '1.6' }}>
                            {loading 
                                ? 'Our AI is analyzing your documents to extract tone, style, and key brand attributes. This usually takes about 30 seconds.' 
                                : `Ready to process ${uploadedDocs.length} document${uploadedDocs.length !== 1 ? 's' : ''}. We'll create a comprehensive system prompt that captures your unique brand identity.`}
                        </p>

                        {loading && (
                            <div style={{ marginBottom: '2rem' }}>
                                <div style={{ 
                                    width: '100%', 
                                    height: '8px', 
                                    backgroundColor: '#f1f5f9', 
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    maxWidth: '400px',
                                    margin: '0 auto 1.5rem',
                                    position: 'relative'
                                }}>
                                    <div style={{ 
                                        width: '40%', 
                                        height: '100%', 
                                        background: 'linear-gradient(90deg, #4285f4, #5c9ff4, #4285f4)',
                                        backgroundSize: '200% 100%',
                                        borderRadius: '4px',
                                        animation: 'shimmer 1.5s ease-in-out infinite'
                                    }} />
                                </div>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    gap: '0.5rem',
                                    alignItems: 'center'
                                }}>
                                    {['Extracting', 'Analyzing', 'Generating'].map((label, i) => (
                                        <div key={label} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.375rem',
                                            padding: '0.375rem 0.75rem',
                                            backgroundColor: i === 0 ? '#e0e7ff' : '#f8fafc',
                                            borderRadius: '9999px',
                                            fontSize: '0.75rem',
                                            fontWeight: '500',
                                            color: i === 0 ? '#4f46e5' : '#94a3b8',
                                            animation: i === 0 ? 'pulse 2s infinite' : 'none'
                                        }}>
                                            {i === 0 && <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />}
                                            {label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {!loading && (
                            <div style={{ marginBottom: '2.5rem' }}>
                                <div style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: '0.75rem', 
                                    padding: '0.75rem 1.25rem',
                                    backgroundColor: '#e8f0fe',
                                    borderRadius: '12px',
                                    color: '#4285f4',
                                    fontWeight: '600',
                                    fontSize: '0.9rem'
                                }}>
                                    <FileSearch size={18} />
                                    {uploadedDocs.length} Source Document{uploadedDocs.length !== 1 ? 's' : ''} Connected
                                </div>
                                
                                {/* Manual Instructions & Strict Rules buttons - Apply to ALL platforms */}
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
                                    <button
                                        onClick={() => setShowInstructionsInput(true)}
                                        style={{
                                            padding: '0.625rem 1rem',
                                            backgroundColor: manualInstructions ? '#f0fdf4' : 'white',
                                            color: manualInstructions ? '#16a34a' : '#64748b',
                                            border: `1px solid ${manualInstructions ? '#16a34a' : '#e2e8f0'}`,
                                            borderRadius: '8px',
                                            fontSize: '0.875rem',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <FileText size={16} />
                                        {manualInstructions ? 'Global Instructions Added' : 'Global Instructions'}
                                    </button>
                                    <button
                                        onClick={() => setShowRulesInput(true)}
                                        style={{
                                            padding: '0.625rem 1rem',
                                            backgroundColor: manualRules ? '#fffbeb' : 'white',
                                            color: manualRules ? '#d97706' : '#64748b',
                                            border: `1px solid ${manualRules ? '#d97706' : '#e2e8f0'}`,
                                            borderRadius: '8px',
                                            fontSize: '0.875rem',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <AlertCircle size={16} />
                                        {manualRules ? 'Global Rules Added' : 'Global Strict Rules'}
                                    </button>
                                </div>
                                {!isUniversalMode && platforms.length > 1 && (
                                    <p style={{ 
                                        color: '#64748b', 
                                        fontSize: '0.75rem', 
                                        textAlign: 'center', 
                                        marginTop: '0.75rem',
                                        fontStyle: 'italic'
                                    }}>
                                        Global rules apply to all selected platforms. Add platform-specific rules below for additional customization.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Platform-Specific Rules Section */}
                        {!loading && (isUniversalMode || platforms.length > 0) && (
                            <div style={{ 
                                marginBottom: '2rem',
                                paddingTop: '2rem',
                                borderTop: '1px solid #e2e8f0'
                            }}>
                                <h4 style={{ 
                                    fontSize: '1rem', 
                                    fontWeight: '600', 
                                    color: '#1e293b', 
                                    marginBottom: '1rem',
                                    textAlign: 'center'
                                }}>
                                    {isUniversalMode ? 'Custom Rules (Optional)' : 'Platform-Specific Rules (Optional)'}
                                </h4>
                                <p style={{ 
                                    color: '#64748b', 
                                    fontSize: '0.875rem', 
                                    textAlign: 'center', 
                                    marginBottom: '1.5rem',
                                    maxWidth: '600px',
                                    margin: '0 auto 1.5rem'
                                }}>
                                    {isUniversalMode 
                                        ? 'Add custom rules to fine-tune the universal brand voice for all platforms.'
                                        : 'Customize rules for each platform to tailor the voice to their unique styles.'}
                                </p>
                                
                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '1rem',
                                    maxWidth: '700px',
                                    margin: '0 auto'
                                }}>
                                    {isUniversalMode ? (
                                        // Universal mode - single rules input
                                        <div>
                                            {!showPlatformRulesInput['UNIVERSAL'] ? (
                                                <button
                                                    onClick={() => setShowPlatformRulesInput({ ...showPlatformRulesInput, 'UNIVERSAL': true })}
                                                    style={{
                                                        width: '100%',
                                                        padding: '1rem',
                                                        backgroundColor: platformRules['UNIVERSAL'] ? '#f0fdf4' : 'white',
                                                        border: `2px dashed ${platformRules['UNIVERSAL'] ? '#16a34a' : '#cbd5e1'}`,
                                                        borderRadius: '12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '0.5rem',
                                                        color: platformRules['UNIVERSAL'] ? '#16a34a' : '#64748b',
                                                        fontWeight: '500',
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    <FileText size={20} />
                                                    {platformRules['UNIVERSAL'] ? 'Edit Custom Rules' : 'Add Custom Rules'}
                                                </button>
                                            ) : (
                                                <div style={{ 
                                                    border: '2px solid #4285f4',
                                                    borderRadius: '12px',
                                                    padding: '1rem',
                                                    backgroundColor: '#f8fafc'
                                                }}>
                                                    <textarea
                                                        value={platformRules['UNIVERSAL'] || ''}
                                                        onChange={(e) => setPlatformRules({ ...platformRules, 'UNIVERSAL': e.target.value })}
                                                        placeholder="e.g., Always mention sustainability, Never use slang, Keep responses under 10 words..."
                                                        style={{
                                                            width: '100%',
                                                            minHeight: '100px',
                                                            padding: '0.75rem',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '8px',
                                                            fontSize: '0.875rem',
                                                            resize: 'vertical',
                                                            marginBottom: '0.75rem'
                                                        }}
                                                    />
                                                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                                        <button
                                                            onClick={() => {
                                                                setShowPlatformRulesInput({ ...showPlatformRulesInput, 'UNIVERSAL': false });
                                                                setPlatformRules({ ...platformRules, 'UNIVERSAL': '' });
                                                            }}
                                                            style={{
                                                                padding: '0.5rem 1rem',
                                                                backgroundColor: 'white',
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: '8px',
                                                                fontSize: '0.875rem',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={() => setShowPlatformRulesInput({ ...showPlatformRulesInput, 'UNIVERSAL': false })}
                                                            style={{
                                                                padding: '0.5rem 1rem',
                                                                backgroundColor: '#4285f4',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                fontSize: '0.875rem',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            Save Rules
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        // Individual platforms mode - dropdown for each selected platform
                                        platforms.map(platform => (
                                            <div key={platform} style={{ 
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '12px',
                                                overflow: 'hidden'
                                            }}>
                                                {!showPlatformRulesInput[platform] ? (
                                                    <button
                                                        onClick={() => setShowPlatformRulesInput({ ...showPlatformRulesInput, [platform]: true })}
                                                        style={{
                                                            width: '100%',
                                                            padding: '1rem',
                                                            backgroundColor: platformRules[platform] ? '#f0fdf4' : 'white',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            color: '#1e293b',
                                                            fontWeight: '500',
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                            {platform === 'INSTAGRAM' && <Instagram size={20} color="#E1306C" />}
                                                            {platform === 'TIKTOK' && <Music size={20} color="#000000" />}
                                                            {platform === 'TWITTER' && <XIcon size={20} color="#000000" />}
                                                            <span>
                                                                {platform === 'INSTAGRAM' ? 'Instagram' 
                                                                : platform === 'TIKTOK' ? 'TikTok'
                                                                : platform === 'TWITTER' ? 'X'
                                                                : platform}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            {platformRules[platform] ? (
                                                                <span style={{ fontSize: '0.875rem', color: '#16a34a' }}>Rules Added</span>
                                                            ) : (
                                                                <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Add Custom Rules</span>
                                                            )}
                                                            <ChevronDown size={16} style={{ 
                                                                transform: showPlatformRulesInput[platform] ? 'rotate(180deg)' : 'rotate(0)',
                                                                transition: 'transform 0.2s'
                                                            }} />
                                                        </div>
                                                    </button>
                                                ) : (
                                                    <div>
                                                        <button
                                                            onClick={() => setShowPlatformRulesInput({ ...showPlatformRulesInput, [platform]: false })}
                                                            style={{
                                                                width: '100%',
                                                                padding: '1rem',
                                                                backgroundColor: '#4285f4',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                color: 'white',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                                {platform === 'INSTAGRAM' && <Instagram size={20} />}
                                                                {platform === 'TIKTOK' && <Music size={20} />}
                                                                {platform === 'TWITTER' && <XIcon size={20} />}
                                                                <span>
                                                                    {platform === 'INSTAGRAM' ? 'Instagram' 
                                                                    : platform === 'TIKTOK' ? 'TikTok'
                                                                    : platform === 'TWITTER' ? 'X'
                                                                    : platform}
                                                                </span>
                                                            </div>
                                                            <ChevronDown size={16} style={{ transform: 'rotate(180deg)' }} />
                                                        </button>
                                                        <div style={{ padding: '1rem', backgroundColor: '#f8fafc' }}>
                                                            <textarea
                                                                value={platformRules[platform] || ''}
                                                                onChange={(e) => setPlatformRules({ ...platformRules, [platform]: e.target.value })}
                                                                placeholder={`Custom rules for ${
                                                                    platform === 'INSTAGRAM' ? 'Instagram' 
                                                                    : platform === 'TIKTOK' ? 'TikTok'
                                                                    : platform === 'TWITTER' ? 'X'
                                                                    : platform
                                                                } (e.g., Use more hashtags, Keep it casual, etc.)`}
                                                                style={{
                                                                    width: '100%',
                                                                    minHeight: '100px',
                                                                    padding: '0.75rem',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: '8px',
                                                                    fontSize: '0.875rem',
                                                                    resize: 'vertical',
                                                                    marginBottom: '0.75rem'
                                                                }}
                                                            />
                                                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                                                <button
                                                                    onClick={() => {
                                                                        setShowPlatformRulesInput({ ...showPlatformRulesInput, [platform]: false });
                                                                        setPlatformRules({ ...platformRules, [platform]: '' });
                                                                    }}
                                                                    style={{
                                                                        padding: '0.5rem 1rem',
                                                                        backgroundColor: 'white',
                                                                        border: '1px solid #e2e8f0',
                                                                        borderRadius: '8px',
                                                                        fontSize: '0.875rem',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    Clear
                                                                </button>
                                                                <button
                                                                    onClick={() => setShowPlatformRulesInput({ ...showPlatformRulesInput, [platform]: false })}
                                                                    style={{
                                                                        padding: '0.5rem 1rem',
                                                                        backgroundColor: '#4285f4',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '8px',
                                                                        fontSize: '0.875rem',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    Done
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        <button 
                            onClick={handleGenerate}
                            disabled={loading}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.75rem',
                                padding: '1rem 2.5rem',
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                backgroundColor: '#4285f4',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                                minWidth: '240px'
                            }}
                        >
                            {loading ? <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={24} />}
                            {loading ? 'Analyzing...' : 'Start Generation'}
                        </button>
                    </div>
                </div>
            )}

            {/* Manual Instructions Modal */}
            {showInstructionsInput && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 50
                }} onClick={() => setShowInstructionsInput(false)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '2rem',
                        width: '90%',
                        maxWidth: '600px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem' }}>Global Instructions</h3>
                        <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.9rem' }}>
                            Add instructions that apply to ALL selected platforms. These work alongside any platform-specific rules.
                        </p>
                        <textarea
                            value={manualInstructions}
                            onChange={(e) => setManualInstructions(e.target.value)}
                            rows={6}
                            placeholder="e.g. Always be friendly and use emojis. Avoid technical jargon."
                            style={{
                                width: '100%',
                                padding: '1rem',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                marginBottom: '1.5rem',
                                resize: 'vertical',
                                fontSize: '0.95rem'
                            }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                            <button
                                onClick={() => setShowInstructionsInput(false)}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    color: '#64748b',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => setShowInstructionsInput(false)}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: '#4285f4',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Rules Modal */}
            {showRulesInput && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 50
                }} onClick={() => setShowRulesInput(false)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '2rem',
                        width: '90%',
                        maxWidth: '600px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem' }}>Global Strict Rules</h3>
                        <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.9rem' }}>
                            Define strict rules that apply to ALL selected platforms. These work alongside any platform-specific rules.
                        </p>
                        <textarea
                            value={manualRules}
                            onChange={(e) => setManualRules(e.target.value)}
                            rows={6}
                            placeholder="e.g. Never mention competitor names. Do not use slang. Keep responses under 280 characters."
                            style={{
                                width: '100%',
                                padding: '1rem',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                marginBottom: '1.5rem',
                                resize: 'vertical',
                                fontSize: '0.95rem'
                            }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                            <button
                                onClick={() => setShowRulesInput(false)}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    color: '#64748b',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => setShowRulesInput(false)}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: '#4285f4',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {step === 'preview' && (
                <div>
                    <div style={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '16px',
                        padding: '2rem',
                        marginBottom: '1.5rem'
                    }}>
                        <h2 style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: '700', 
                            marginBottom: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                        }}>
                            <Eye size={24} />
                            Review & Save Brand Voices
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '2rem' }}>
                            Select which brand voices you'd like to save. You can edit names and review prompts before saving.
                        </p>

                        {/* Scrollable list of all brand voices */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                            {Object.keys(generatedPrompts).map((platform) => {
                                const platformData = generatedPrompts[platform];
                                const platformName = platform === 'UNIVERSAL' ? 'Universal' 
                                                   : platform === 'INSTAGRAM' ? 'Instagram' 
                                                   : platform === 'TIKTOK' ? 'TikTok'
                                                   : platform === 'TWITTER' ? 'X'
                                                   : platform;
                                const icon = platform === 'UNIVERSAL' ? <Globe size={18} />
                                          : platform === 'INSTAGRAM' ? <Instagram size={18} /> 
                                          : platform === 'TIKTOK' ? <Music size={18} />
                                          : <XIcon size={18} />;
                                const isSelected = selectedVoices[platform] || false;
                                const isExpanded = expandedVoices[platform] || false;

                                return (
                                    <div 
                                        key={platform}
                                        style={{
                                            border: `2px solid ${isSelected ? '#4285f4' : '#e2e8f0'}`,
                                            borderRadius: '12px',
                                            padding: '1.5rem',
                                            backgroundColor: isSelected ? '#f8fbff' : 'white',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {/* Header with checkbox and platform */}
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={(e) => {
                                                    setSelectedVoices({
                                                        ...selectedVoices,
                                                        [platform]: e.target.checked
                                                    });
                                                }}
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    cursor: 'pointer',
                                                    marginTop: '2px'
                                                }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                                    {icon}
                                                    <span style={{ 
                                                        fontWeight: '600', 
                                                        fontSize: '1rem',
                                                        color: isSelected ? '#4285f4' : '#1e293b'
                                                    }}>
                                                        {platformName} Brand Voice
                                                    </span>
                                                    <span style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: '600',
                                                        color: '#4285f4',
                                                        backgroundColor: '#e8f0fe',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #4285f4'
                                                    }}>
                                                        {platformName}
                                                    </span>
                                                </div>
                                                
                                                {/* Name input */}
                                                <div style={{ marginBottom: '1rem' }}>
                                                    <label style={{
                                                        display: 'block',
                                                        fontSize: '0.875rem',
                                                        fontWeight: '500',
                                                        marginBottom: '0.5rem',
                                                        color: '#64748b'
                                                    }}>
                                                        Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={voiceNames[platform] || ''}
                                                        onChange={(e) => {
                                                            setVoiceNames({
                                                                ...voiceNames,
                                                                [platform]: e.target.value
                                                            });
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            padding: '0.75rem',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '8px',
                                                            fontSize: '0.95rem'
                                                        }}
                                                        placeholder={`My Brand Voice - ${platformName}`}
                                                    />
                                                </div>

                                                {/* Brand Summary */}
                                                {platformData.brand_summary && (
                                                    <div style={{
                                                        backgroundColor: '#e8f0fe',
                                                        padding: '0.75rem',
                                                        borderRadius: '8px',
                                                        marginBottom: '1rem'
                                                    }}>
                                                        <strong style={{ fontSize: '0.8rem', color: '#1e40af' }}>Brand Summary:</strong>
                                                        <p style={{ color: '#1e40af', fontSize: '0.8rem', marginTop: '0.25rem', lineHeight: '1.5' }}>
                                                            {platformData.brand_summary}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Expand/Collapse prompt */}
                                                <button
                                                    onClick={() => {
                                                        setExpandedVoices({
                                                            ...expandedVoices,
                                                            [platform]: !isExpanded
                                                        });
                                                    }}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        padding: '0.5rem 0.75rem',
                                                        backgroundColor: 'transparent',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '0.875rem',
                                                        color: '#64748b',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronDown size={16} style={{ transform: 'rotate(-90deg)' }} />}
                                                    {isExpanded ? 'Hide' : 'View'} System Prompt
                                                </button>

                                                {isExpanded && (
                                                    <div style={{ marginTop: '1rem' }}>
                                                        <textarea
                                                            value={platformData.system_prompt}
                                                            readOnly
                                                            rows={10}
                                                            style={{
                                                                width: '100%',
                                                                padding: '1rem',
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: '8px',
                                                                fontSize: '0.8rem',
                                                                fontFamily: 'monospace',
                                                                lineHeight: '1.5',
                                                                backgroundColor: '#f8fafc',
                                                                color: '#334155',
                                                                resize: 'vertical'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <button 
                                onClick={handleSave}
                                disabled={loading || Object.values(selectedVoices).filter(Boolean).length === 0}
                                className="primary"
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    padding: '1rem',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    opacity: Object.values(selectedVoices).filter(Boolean).length === 0 ? 0.5 : 1,
                                    cursor: Object.values(selectedVoices).filter(Boolean).length === 0 ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {loading ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={18} />}
                                {loading ? 'Saving...' : `Save ${Object.values(selectedVoices).filter(Boolean).length} Brand Voice${Object.values(selectedVoices).filter(Boolean).length === 1 ? '' : 's'}`}
                            </button>
                            
                            <button 
                                onClick={handleDiscardAll}
                                disabled={loading}
                                style={{
                                    padding: '1rem 1.5rem',
                                    backgroundColor: '#fee2e2',
                                    color: '#dc2626',
                                    border: '2px solid #dc2626',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.currentTarget.style.backgroundColor = '#dc2626';
                                        e.currentTarget.style.color = 'white';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!loading) {
                                        e.currentTarget.style.backgroundColor = '#fee2e2';
                                        e.currentTarget.style.color = '#dc2626';
                                    }
                                }}
                            >
                                <X size={18} />
                                Discard All
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {step === 'history' && (
                <div>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '2rem',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                    }}>
                        <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                            Switch between different brand voice modes or edit existing ones
                        </p>

                        {/* Platform Filter Tabs */}
                        <div style={{ 
                            display: 'flex', 
                            gap: '0.5rem', 
                            marginBottom: '1.5rem',
                            borderBottom: '2px solid #e2e8f0',
                            paddingBottom: '0.5rem',
                            flexWrap: 'wrap'
                        }}>
                            {['UNIVERSAL', 'INSTAGRAM', 'TIKTOK', 'TWITTER'].map(filter => {
                                const isActive = platformFilter === filter;
                                const getIcon = () => {
                                    if (filter === 'INSTAGRAM') return <Instagram size={16} />;
                                    if (filter === 'TIKTOK') return <Music size={16} />;
                                    if (filter === 'TWITTER') return <XIcon size={16} />;
                                    return null;
                                };
                                
                                const getLabel = () => {
                                    if (filter === 'UNIVERSAL') return 'Universal';
                                    if (filter === 'INSTAGRAM') return 'Instagram';
                                    if (filter === 'TIKTOK') return 'TikTok';
                                    if (filter === 'TWITTER') return 'X';
                                    return filter;
                                };

                                return (
                                    <button
                                        key={filter}
                                        onClick={() => setPlatformFilter(filter)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            padding: '0.5rem 1rem',
                                            backgroundColor: isActive ? '#4285f4' : 'transparent',
                                            color: isActive ? 'white' : '#64748b',
                                            border: `2px solid ${isActive ? '#4285f4' : '#e2e8f0'}`,
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: isActive ? '600' : '500',
                                            fontSize: '0.875rem',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.borderColor = '#4285f4';
                                                e.currentTarget.style.color = '#4285f4';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.borderColor = '#e2e8f0';
                                                e.currentTarget.style.color = '#64748b';
                                            }
                                        }}
                                    >
                                        {getIcon()}
                                        {getLabel()}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Toggle and Generate New Button Row */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1.5rem'
                        }}>
                            {/* Left side: Toggle for both Universal and platform-specific tabs */}
                            <div>
                                {platformFilter === 'UNIVERSAL' ? (
                                    // Universal tab: "Set all platforms to use universal"
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const allPlatformsUsingUniversal = ['INSTAGRAM', 'TIKTOK', 'TWITTER'].every(p => platformToggles[p]);
                                                console.log('All platforms checkbox click - current:', allPlatformsUsingUniversal, 'new:', !allPlatformsUsingUniversal);
                                                handleSetAllPlatformsToUniversal(!allPlatformsUsingUniversal);
                                            }}
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '4px',
                                                border: `2px solid ${['INSTAGRAM', 'TIKTOK', 'TWITTER'].every(p => platformToggles[p]) ? '#4285f4' : '#cbd5e1'}`,
                                                backgroundColor: ['INSTAGRAM', 'TIKTOK', 'TWITTER'].every(p => platformToggles[p]) ? '#4285f4' : 'white',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.2s',
                                                padding: 0,
                                                flexShrink: 0
                                            }}
                                        >
                                            {['INSTAGRAM', 'TIKTOK', 'TWITTER'].every(p => platformToggles[p]) && (
                                                <CheckCircle size={14} color="white" />
                                            )}
                                        </button>
                                        <span style={{
                                            fontSize: '0.9rem',
                                            fontWeight: '500',
                                            color: '#1e293b',
                                            userSelect: 'none'
                                        }}>
                                            Set All Platforms to Use Universal Voice
                                        </span>
                                    </div>
                                ) : (
                                    // Platform-specific tabs: "Use Universal Brand Voice"
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const currentState = platformToggles[platformFilter] ?? false;
                                                console.log('Universal checkbox click - current:', currentState, 'new:', !currentState);
                                                handlePlatformToggle(platformFilter, !currentState);
                                            }}
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '4px',
                                                border: `2px solid ${platformToggles[platformFilter] ? '#4285f4' : '#cbd5e1'}`,
                                                backgroundColor: platformToggles[platformFilter] ? '#4285f4' : 'white',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.2s',
                                                padding: 0,
                                                flexShrink: 0
                                            }}
                                        >
                                            {platformToggles[platformFilter] && (
                                                <CheckCircle size={14} color="white" />
                                            )}
                                        </button>
                                        <span style={{
                                            fontSize: '0.9rem',
                                            fontWeight: '500',
                                            color: '#1e293b',
                                            userSelect: 'none'
                                        }}>
                                            Use Universal Brand Voice
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {editingMode ? (
                            <div style={{ marginBottom: '1.5rem' }}>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                                        Mode Name
                                    </label>
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: '0.95rem'
                                        }}
                                    />
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                                        Target Platforms
                                    </label>
                                    <div 
                                        onClick={() => setShowEditPlatformDropdown(!showEditPlatformDropdown)}
                                        style={{
                                            padding: '0.75rem',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: '0.95rem',
                                            backgroundColor: 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            position: 'relative'
                                        }}
                                    >
                                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {editPlatforms.length === 3 
                                                ? 'All Platforms' 
                                                : editPlatforms.length === 0 
                                                ? 'Select Platform' 
                                                : editPlatforms.length === 1
                                                ? (editPlatforms[0] === 'INSTAGRAM' ? 'Instagram' : editPlatforms[0] === 'TIKTOK' ? 'TikTok' : 'X')
                                                : `${editPlatforms.length} Platforms`
                                            }
                                        </span>
                                        <ChevronDown size={16} color="var(--text-secondary)" />
                                        {showEditPlatformDropdown && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                marginTop: '4px',
                                                backgroundColor: 'white',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: 'var(--radius-md)',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                zIndex: 10,
                                                padding: '0.5rem'
                                            }}>
                                                <div 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const has = editPlatforms.includes('INSTAGRAM');
                                                        setEditPlatforms(has ? editPlatforms.filter(p => p !== 'INSTAGRAM') : [...editPlatforms, 'INSTAGRAM']);
                                                    }}
                                                    style={{
                                                        padding: '0.5rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        cursor: 'pointer',
                                                        borderRadius: '4px',
                                                        backgroundColor: editPlatforms.includes('INSTAGRAM') ? '#e8f0fe' : 'transparent'
                                                    }}
                                                >
                                                    <div style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        border: `1px solid ${editPlatforms.includes('INSTAGRAM') ? '#4285f4' : '#cbd5e1'}`,
                                                        borderRadius: '4px',
                                                        backgroundColor: editPlatforms.includes('INSTAGRAM') ? '#4285f4' : 'white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {editPlatforms.includes('INSTAGRAM') && <CheckCircle size={12} color="white" />}
                                                    </div>
                                                    <span style={{ fontSize: '0.9rem' }}>Instagram</span>
                                                </div>
                                                <div 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const has = editPlatforms.includes('TIKTOK');
                                                        setEditPlatforms(has ? editPlatforms.filter(p => p !== 'TIKTOK') : [...editPlatforms, 'TIKTOK']);
                                                    }}
                                                    style={{
                                                        padding: '0.5rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        cursor: 'pointer',
                                                        borderRadius: '4px',
                                                        backgroundColor: editPlatforms.includes('TIKTOK') ? '#e8f0fe' : 'transparent'
                                                    }}
                                                >
                                                    <div style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        border: `1px solid ${editPlatforms.includes('TIKTOK') ? '#4285f4' : '#cbd5e1'}`,
                                                        borderRadius: '4px',
                                                        backgroundColor: editPlatforms.includes('TIKTOK') ? '#4285f4' : 'white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {editPlatforms.includes('TIKTOK') && <CheckCircle size={12} color="white" />}
                                                    </div>
                                                    <span style={{ fontSize: '0.9rem' }}>TikTok</span>
                                                </div>
                                                <div 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const has = editPlatforms.includes('TWITTER');
                                                        setEditPlatforms(has ? editPlatforms.filter(p => p !== 'TWITTER') : [...editPlatforms, 'TWITTER']);
                                                    }}
                                                    style={{
                                                        padding: '0.5rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        cursor: 'pointer',
                                                        borderRadius: '4px',
                                                        backgroundColor: editPlatforms.includes('TWITTER') ? '#e8f0fe' : 'transparent'
                                                    }}
                                                >
                                                    <div style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        border: `1px solid ${editPlatforms.includes('TWITTER') ? '#4285f4' : '#cbd5e1'}`,
                                                        borderRadius: '4px',
                                                        backgroundColor: editPlatforms.includes('TWITTER') ? '#4285f4' : 'white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {editPlatforms.includes('TWITTER') && <CheckCircle size={12} color="white" />}
                                                    </div>
                                                    <span style={{ fontSize: '0.9rem' }}>X</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                                        System Instructions
                                    </label>
                                    <textarea
                                        value={editSystemPrompt}
                                        onChange={(e) => setEditSystemPrompt(e.target.value)}
                                        rows={12}
                                        style={{
                                            width: '100%',
                                            padding: '1rem',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: '0.875rem',
                                            fontFamily: 'monospace',
                                            lineHeight: '1.6',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: '0.75rem' }}>
                                    <button onClick={handleUpdateMode} disabled={loading} className="primary" style={{ flex: 1 }}>
                                        {loading ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                                        <span style={{ marginLeft: '0.5rem' }}>Save Changes</span>
                                    </button>
                                    <button onClick={() => setEditingMode(null)} style={{ padding: '0.75rem 1.5rem' }}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '0.75rem'
                            }}>
                                {(() => {
                                    // Filter brand modes based on selected platform
                                    console.log('🔍 FILTERING VOICES');
                                    console.log('Platform filter:', platformFilter);
                                    console.log('Total brand modes:', brandModes.length);
                                    console.log('All modes:', brandModes.map(m => ({ id: m.id, name: m.name, platforms: m.platforms })));
                                    
                                    const filteredModes = brandModes.filter((mode: BrandMode) => {
                                        if (platformFilter === 'UNIVERSAL') {
                                            // Universal tab shows ONLY truly universal voices:
                                            // - Voices with NO platform specified (platforms is null/undefined/empty)
                                            // - Voices with 2 or MORE platforms (multi-platform)
                                            // EXCLUDE single-platform voices (platforms.length === 1)
                                            const isUniversal = !mode.platforms || mode.platforms.length === 0 || mode.platforms.length >= 2;
                                            console.log(`  ${mode.name}: platforms=${JSON.stringify(mode.platforms)}, isUniversal=${isUniversal}`);
                                            return isUniversal;
                                        }
                                        // For specific platforms, show ONLY voices with EXACTLY that single platform
                                        const isPlatformSpecific = mode.platforms && mode.platforms.length === 1 && mode.platforms.includes(platformFilter);
                                        console.log(`  ${mode.name}: platforms=${JSON.stringify(mode.platforms)}, isPlatformSpecific=${isPlatformSpecific}`);
                                        return isPlatformSpecific;
                                    });
                                    
                                    console.log('Filtered modes count:', filteredModes.length);
                                    console.log('Filtered modes:', filteredModes.map(m => ({ id: m.id, name: m.name })));

                                    if (filteredModes.length === 0) {
                                        return (
                                            <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>
                                                {brandModes.length === 0 
                                                    ? 'No modes saved yet. Generate and save your first brand voice mode!'
                                                    : `No ${platformFilter === 'UNIVERSAL' ? 'universal' : platformFilter.toLowerCase()} brand voices found.`
                                                }
                                            </p>
                                        );
                                    }

                                    return filteredModes.map((mode: BrandMode) => {
                                        // Determine if this voice should visually appear as active
                                        const isVisuallyActive = platformFilter === 'UNIVERSAL'
                                            ? selectedUniversalVoiceId === mode.id
                                            : selectedPlatformVoiceIds[platformFilter] === mode.id && !platformToggles[platformFilter];
                                        
                                        return (
                                        <div
                                            key={mode.id}
                                            style={{
                                                padding: '1rem',
                                                border: isVisuallyActive ? '2px solid #4285f4' : '1px solid #e2e8f0',
                                                borderRadius: '12px',
                                                backgroundColor: isVisuallyActive ? '#f5f3ff' : 'white',
                                                transition: 'all 0.2s ease',
                                                boxShadow: isVisuallyActive ? '0 4px 12px rgba(99, 102, 241, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
                                                display: 'flex',
                                                gap: '1rem',
                                                alignItems: 'flex-start'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isVisuallyActive) {
                                                    e.currentTarget.style.borderColor = '#c7d2fe';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isVisuallyActive) {
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                                                }
                                            }}
                                        >
                                            {/* Checkbox on the left edge */}
                                            <div style={{ flexShrink: 0, paddingTop: '0.25rem' }}>
                                                {platformFilter === 'UNIVERSAL' ? (
                                                    // Universal voice - checkbox
                                                    <button
                                                        onClick={() => handleSelectUniversalVoice(mode.id)}
                                                        style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            borderRadius: '4px',
                                                            border: `2px solid ${selectedUniversalVoiceId === mode.id ? '#4285f4' : '#cbd5e1'}`,
                                                            backgroundColor: selectedUniversalVoiceId === mode.id ? '#4285f4' : 'white',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'all 0.2s',
                                                            padding: 0
                                                        }}
                                                    >
                                                        {selectedUniversalVoiceId === mode.id && (
                                                            <CheckCircle size={14} color="white" />
                                                        )}
                                                    </button>
                                                ) : (
                                                    // Platform-specific voice - checkbox
                                                    <button
                                                        onClick={() => handleSelectPlatformVoice(platformFilter, mode.id)}
                                                        style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            borderRadius: '4px',
                                                            border: `2px solid ${selectedPlatformVoiceIds[platformFilter] === mode.id ? '#4285f4' : '#cbd5e1'}`,
                                                            backgroundColor: selectedPlatformVoiceIds[platformFilter] === mode.id ? '#4285f4' : 'white',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'all 0.2s',
                                                            padding: 0
                                                        }}
                                                    >
                                                        {selectedPlatformVoiceIds[platformFilter] === mode.id && (
                                                            <CheckCircle size={14} color="white" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>

                                            {/* Content area */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                {/* Name and badges */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                                    <strong style={{ fontSize: '0.95rem' }}>{mode.name}</strong>
                                                    {isVisuallyActive && (
                                                        <span style={{
                                                            padding: '0.25rem 0.625rem',
                                                            background: 'linear-gradient(135deg, #4285f4 0%, #5c9ff4 100%)',
                                                            color: 'white',
                                                            borderRadius: '9999px',
                                                            fontSize: '0.7rem',
                                                            fontWeight: '600',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.25rem',
                                                            boxShadow: '0 2px 6px rgba(99, 102, 241, 0.3)'
                                                        }}>
                                                            <Star size={10} />
                                                            ACTIVE
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Platform badges - only show on Universal tab */}
                                                {platformFilter === 'UNIVERSAL' && mode.platforms && mode.platforms.length > 0 && (
                                                    <div style={{ display: 'flex', gap: '4px', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                                        {mode.platforms.map(p => {
                                                            const getPlatformStyle = () => {
                                                                if (p === 'INSTAGRAM') return { bg: '#fce7f3', color: '#be185d' };
                                                                if (p === 'TIKTOK') return { bg: '#e0f2fe', color: '#0369a1' };
                                                                if (p === 'TWITTER') return { bg: '#dbeafe', color: '#1e40af' };
                                                                return { bg: '#e5e7eb', color: '#374151' };
                                                            };
                                                            const style = getPlatformStyle();
                                                            
                                                            return (
                                                                <span key={p} style={{
                                                                    fontSize: '0.65rem',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: style.bg,
                                                                    color: style.color,
                                                                    fontWeight: '600',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '2px'
                                                                }}>
                                                                    {                                                                    p === 'INSTAGRAM' ? (
                                                                        <Instagram size={12} />
                                                                    ) : p === 'TIKTOK' ? (
                                                                        <Music size={12} />
                                                                    ) : p === 'TWITTER' ? (
                                                                        <XIcon size={12} />
                                                                    ) : null}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* Brand summary */}
                                                {mode.brand_summary && (
                                                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                                                        {mode.brand_summary.substring(0, 150)}...
                                                    </p>
                                                )}

                                                {/* Meta information */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                        <Clock size={12} />
                                                        {new Date(mode.created_at).toLocaleDateString()}
                                                    </span>
                                                    {mode.tone_attributes && mode.tone_attributes.length > 0 && (
                                                        <span>
                                                            {mode.tone_attributes.slice(0, 3).join(', ')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Action buttons on the right */}
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flexShrink: 0 }}>
                                                {/* Favorite button */}
                                                <button
                                                    onClick={() => toggleFavorite(mode.id)}
                                                    style={{
                                                        padding: '0.5rem',
                                                        backgroundColor: 'transparent',
                                                        border: '1px solid #fbbf24',
                                                        borderRadius: 'var(--radius-sm)',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        position: 'relative'
                                                    }}
                                                    title="Set Favorite"
                                                    onMouseEnter={(e) => {
                                                        const tooltip = e.currentTarget.querySelector('.favorite-tooltip') as HTMLElement;
                                                        if (tooltip) tooltip.style.opacity = '1';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        const tooltip = e.currentTarget.querySelector('.favorite-tooltip') as HTMLElement;
                                                        if (tooltip) tooltip.style.opacity = '0';
                                                    }}
                                                >
                                                    <Star 
                                                        size={14} 
                                                        fill={favoriteModes.has(mode.id) ? '#fbbf24' : 'transparent'}
                                                        color="#fbbf24"
                                                        style={{ 
                                                            transition: 'all 0.2s',
                                                            transform: favoriteModes.has(mode.id) ? 'scale(1.1)' : 'scale(1)'
                                                        }}
                                                    />
                                                    <span 
                                                        className="favorite-tooltip"
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: '100%',
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            marginBottom: '0.25rem',
                                                            padding: '0.25rem 0.5rem',
                                                            backgroundColor: '#1e293b',
                                                            color: 'white',
                                                            fontSize: '0.7rem',
                                                            borderRadius: '4px',
                                                            whiteSpace: 'nowrap',
                                                            opacity: 0,
                                                            transition: 'opacity 0.2s',
                                                            pointerEvents: 'none',
                                                            zIndex: 10
                                                        }}
                                                    >
                                                        Set Favorite
                                                    </span>
                                                </button>
                                                <button
                                                    onClick={() => startEditMode(mode)}
                                                    style={{
                                                        padding: '0.5rem',
                                                        backgroundColor: 'transparent',
                                                        border: '1px solid var(--border-color)',
                                                        borderRadius: 'var(--radius-sm)',
                                                        cursor: 'pointer'
                                                    }}
                                                    title="Edit Mode"
                                                >
                                                    <Edit3 size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteMode(mode.id)}
                                                    disabled={loading}
                                                    style={{
                                                        padding: '0.5rem',
                                                        backgroundColor: 'transparent',
                                                        border: '1px solid #fecaca',
                                                        borderRadius: 'var(--radius-sm)',
                                                        color: '#dc2626',
                                                        cursor: loading ? 'not-allowed' : 'pointer'
                                                    }}
                                                    title="Delete Mode"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                    });
                                })()}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal: No Platform Voices Found */}
            {showNoPlatformVoiceModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}
                onClick={() => setShowNoPlatformVoiceModal(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            padding: '2rem',
                            maxWidth: '500px',
                            width: '90%',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                backgroundColor: '#fef3c7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <AlertCircle size={28} color="#f59e0b" />
                            </div>
                            <div>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '1.25rem',
                                    fontWeight: '700',
                                    color: '#1e293b'
                                }}>
                                    No {showNoPlatformVoiceModal.charAt(0) + showNoPlatformVoiceModal.slice(1).toLowerCase()} Voices Found
                                </h3>
                                <p style={{
                                    margin: '0.25rem 0 0 0',
                                    fontSize: '0.875rem',
                                    color: '#64748b'
                                }}>
                                    You need to create a {showNoPlatformVoiceModal.toLowerCase()} voice first
                                </p>
                            </div>
                        </div>

                        <p style={{
                            margin: '0 0 1.5rem 0',
                            fontSize: '0.95rem',
                            color: '#475569',
                            lineHeight: '1.6'
                        }}>
                            Would you like to generate a new {showNoPlatformVoiceModal.toLowerCase()} brand voice, or continue using the universal voice?
                        </p>

                        <div style={{
                            display: 'flex',
                            gap: '0.75rem'
                        }}>
                            <button
                                onClick={() => {
                                    // Go to generation with platform pre-selected
                                    setIsUniversalMode(false);
                                    setPlatforms([showNoPlatformVoiceModal]);
                                    setStep('platforms');
                                    setShowNoPlatformVoiceModal(null);
                                    if (onRegeneratePlatform) {
                                        onRegeneratePlatform(showNoPlatformVoiceModal);
                                    }
                                }}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                <span style={{ fontSize: '1.2rem', lineHeight: '1' }}>+</span>
                                Generate New Voice
                            </button>
                            <button
                                onClick={() => {
                                    // Keep universal - toggle back ON
                                    setPlatformToggles(prev => ({
                                        ...prev,
                                        [showNoPlatformVoiceModal]: true
                                    }));
                                    setShowNoPlatformVoiceModal(null);
                                }}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: 'white',
                                    color: '#64748b',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Keep Universal
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.7; transform: scale(1.05); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes progress {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                @keyframes glow {
                    0%, 100% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.3); }
                    50% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.6); }
                }
            `}</style>
        </div>
    );
};

export default BrandVoiceSettings;
